import type { EditionBusinessKey } from "@/consts/businesses";

/**
 * Public profile slug -> Openings member id for Eddie's Edition.
 *
 * This is an identity map, not a location roster. Openings does not expose
 * the public `userPathId` used by `/stylists/:id`, and the same person has a
 * different member id in each business. Which locations a member currently
 * works at is discovered from each live schedule's `members` collection.
 */
export const EDDIES_STYLISTS: Record<string, string> = {
  tina: "cmmnrb0n0000804jsh3kofjcp",
  jojo: "cmmnrbybk000a04jsrsx2ci5v",
  erin: "cmmnrdcik000c04jsc9wj9toj",
  jinha: "cmmnrgh28000k04jsok15sl8n",
  carla: "cmmnrj5d4000p04jsczz786z4",
  olivia: "cmmnrflew000i04jseeq2rkvi",
  kristy: "cmmnri8pi000n04jsjktpxmhj",
  kyla: "cmmnqz0yo000204jun6pmqncl",
  will: "cmmnrbkws000904jsvn5o1dex",
  elena: "cmqa9bj31000004l1qrs112gj",
  christine: "cmmnr41z2000504jusxvvcyae",
  arnold: "cmmnr6e83000104js7kweduc2",
  arthur: "cmrhzzj1p000004ibexduv71m",
  connie: "ee-tm-cmmnqtfl9000004l8inkvv4i8-ELWkMaSLLwawBomcj",
  yumi: "cmmnrhoko000m04js4sbhaay8",
  jane: "cmmnrjgs4000q04jsx6ag1e4p",
  celine: "cmmnrju12000r04jsaus5h8u4",
  sean: "cmmnrcbnq000b04jsm088mh61",
  ben: "cmqk05u4o000004kv9quaaq32",
  grayson: "cmmnqwyrl000004ju46vnrrjk",
  harin: "cmmnr74f1000204jsgcnv5mp6",
  egan: "cmmnr9an3000504jsjkpus9pq",
  muneo: "cmmnr9uem000604js9c76wsg2",
  hanna: "cmmnre6ne000e04js18hxeqwx",
  hin: "cmmnrfa0z000h04jso8i4etxx",
  don: "cmmnrg5kl000j04js9b4rxtkc",
  melvin: "cmmnrh8u5000l04jsevu06vos",
  danny: "cmmnriolo000o04jsmr50630f",
  kazunori: "cmmnqyhjr000104ju03dimdfq",
  ray: "cmu99awux000004ig91j6hj19",
  royce: "cmmnqzglg000304jux60xj29n",
  jun: "cmmnr02yy000404juqlp1znvc",
  heidi: "cmmnr7yc9000304jsm2s778tc",
  ashley: "cmmnrakg1000704jssxe5nipb",
  yuna: "cmmnrelhx000f04jsgnke1jsa",
  victor: "cmmnrexqa000g04js4esjbar5",
  eddie: "cmmrzdgyc000104kzmawa7d9r",
};

/** Public profile slug -> Openings member id for Edition Studio. */
export const EDITION_STUDIO_STYLISTS: Record<string, string> = {
  faye: "cmmjs37ch000004l1w4p9ghvo",
  eddie: "cmm1rrqe500004355vldsh7vf",
  joanne: "cmmjs3urq000004lb3hfflesf",
  kimmie: "cmmqvmkqj000204k06aerht5i",
  peter: "cmphs2dg2000004kz9oauih2x",
  christine: "cmoyo9a5u000004jxjtjss3g8",
};

/**
 * The only static routing boundary: a public profile identity within a
 * business. Location membership is intentionally absent from this registry.
 */
export const STYLISTS_BY_BUSINESS: Record<
  EditionBusinessKey,
  Record<string, string>
> = {
  eddies: EDDIES_STYLISTS,
  "edition-studio": EDITION_STUDIO_STYLISTS,
};

/**
 * Which business owns the canonical `/stylists/<pathId>` profile when the
 * person has identities in more than one business.
 */
export const STYLIST_CANONICAL_BUSINESS: Partial<
  Record<string, EditionBusinessKey>
> = {
  christine: "eddies",
  eddie: "edition-studio",
};

/**
 * Public profile slug -> display name for stylists who have left. A departed
 * stylist keeps their identity entry above so past appointments still
 * resolve; this list only swaps their `/stylists/<pathId>` profile for a
 * "no longer with us" notice. Openings stops returning a deactivated member,
 * so the name has to live here.
 */
export const DEPARTED_STYLISTS: Record<string, string> = {
  ben: "Benny",
};

export const getDepartedStylistName = (
  userPathId: string,
): string | undefined => {
  const key = userPathId.toLowerCase();
  return Object.prototype.hasOwnProperty.call(DEPARTED_STYLISTS, key)
    ? DEPARTED_STYLISTS[key]
    : undefined;
};

export const isEditionStudioStylist = (userPathId: string): boolean =>
  Object.prototype.hasOwnProperty.call(
    EDITION_STUDIO_STYLISTS,
    userPathId.toLowerCase(),
  );

export const getEditionStudioMemberId = (
  userPathId: string,
): string | undefined => EDITION_STUDIO_STYLISTS[userPathId.toLowerCase()];

export const isOpeningsBackedStylist = (userPathId: string): boolean =>
  Object.values(STYLISTS_BY_BUSINESS).some((stylists) =>
    Object.prototype.hasOwnProperty.call(stylists, userPathId.toLowerCase()),
  );

export const getStylistMemberId = (
  userPathId: string,
  business: EditionBusinessKey,
): string | undefined =>
  STYLISTS_BY_BUSINESS[business][userPathId.toLowerCase()];

export const getBusinessForMemberId = (
  memberId: string | null | undefined,
): EditionBusinessKey | undefined => {
  if (!memberId) return undefined;
  return (
    Object.entries(STYLISTS_BY_BUSINESS) as [
      EditionBusinessKey,
      Record<string, string>,
    ][]
  ).find(([, stylists]) => Object.values(stylists).includes(memberId))?.[0];
};

/** Openings member id -> public Edition profile slug. */
export const getEditionUserPathIdFromMemberId = (
  memberId: string | null | undefined,
): string | undefined => {
  if (!memberId) return undefined;
  for (const stylists of Object.values(STYLISTS_BY_BUSINESS)) {
    for (const [pathId, id] of Object.entries(stylists)) {
      if (id === memberId) return pathId;
    }
  }
  return undefined;
};
